import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {commonProviderDomains, extractDomainFromEmail} from '../../../helpers/domainHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {DomainModel, DomainValidityStatusesEnum} from '../../../models/api/domainModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {
  ContactDetailsPanelSubState,
  subStateIsAccountDetailsSubState,
} from '../../../store/panels/contacts/contactDetailsPanelSubState';
import {Skeleton} from '../../common/skeleton';
import {Tab} from '../../common/tabs/tab';
import {TabsWrapper} from '../../common/tabs/tabsWrapper';
import {
  buildSwitchTabsPayload,
  ContactDetailsTrackingDisplayContextOptionsEnum,
} from '../contactDetailsTrackingHelpers';

/*
 * Constants.
 */

const minimumNameLengthToOverflow = 10;

/*
 * Props.
 */

interface ContactPluginHeaderTabsProps {
  selectedVisualContact: VisualContactModel;
  selectedCard?: CardModel;
  account?: AccountModel;
  subState?: ContactDetailsPanelSubState;
  selectedDomain?: DomainModel;
  namespace: string;

  selectContactTab: Handler;
  selectAccountTab: Handler;
  onRenderContactSwitcher: Renderer;
}

/*
 * Style.
 */

const StyledPluginHeaderActionsContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledTabsWrapper = styled(TabsWrapper)`
  flex: 1;
  padding-right: 16px;
  padding-top: 27px;
`;

const StyledContactSwitcherContainerDiv = styled.div`
  flex: 0 0 54px;

  > div {
    margin-top: 21px;
  }

  &:empty {
    flex: 0;
    margin-left: -16px;
  }
`;

/*
 * Component.
 */

export const ContactPluginHeaderTabs: FC<ContactPluginHeaderTabsProps> = (props) => {
  const {
    account,
    namespace,
    selectedCard,
    selectedVisualContact,
    subState,
    selectedDomain,
    selectContactTab,
    selectAccountTab,
    onRenderContactSwitcher,
  } = props;

  const onSelectContactTabHandler = () => {
    selectContactTab();
    trackInterfaceEvent(
      InterfaceEventsEnum.P4_CONTACT_DETAILS_SWITCH_TABS,
      buildSwitchTabsPayload(namespace, ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT),
    );
  };
  const onSelectAccountTabHandler = () => {
    selectAccountTab();
    trackInterfaceEvent(
      InterfaceEventsEnum.P4_CONTACT_DETAILS_SWITCH_TABS,
      buildSwitchTabsPayload(namespace, ContactDetailsTrackingDisplayContextOptionsEnum.ACCOUNT, account?.id),
    );
  };

  const isPrivateContact = Boolean(selectedCard && isTeammateNamespace(selectedCard.namespace));
  const contactTabName = constructContactTabName(selectedCard, selectedVisualContact, isPrivateContact);
  const accountTabName = constructAccountTabName(
    selectedCard,
    selectedVisualContact,
    account,
    selectedDomain,
  );

  const shouldShowAccountTabLoading = Boolean(
    selectedDomain?.validityStatus === DomainValidityStatusesEnum.TO_BE_CHECKED ||
      (selectedCard?.accountId && !account),
  );

  return (
    <StyledPluginHeaderActionsContainerDiv>
      <StyledTabsWrapper>
        <Tab
          isSelected={!subState}
          isDisabled={isPrivateContact}
          isOverflowDisabled={contactTabName.length <= minimumNameLengthToOverflow}
          label={contactTabName}
          onClick={onSelectContactTabHandler}
        >
          {contactTabName}
        </Tab>
        {maybeRenderAccountTab(
          accountTabName,
          isPrivateContact,
          subState,
          shouldShowAccountTabLoading,
          onSelectAccountTabHandler,
        )}
      </StyledTabsWrapper>
      <StyledContactSwitcherContainerDiv>{onRenderContactSwitcher()}</StyledContactSwitcherContainerDiv>
    </StyledPluginHeaderActionsContainerDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderAccountTab(
  accountTabName: string | ReactNode,
  isPrivateContact: boolean,
  subState: ContactDetailsPanelSubState | undefined,
  shouldShowAccountTabLoading: boolean,
  selectAccountTab: Handler,
) {
  if (isPrivateContact) {
    return null;
  }
  if (shouldShowAccountTabLoading) {
    return <Skeleton width="88px" height="16px" />;
  }
  return (
    <Tab
      isSelected={Boolean(subState && subStateIsAccountDetailsSubState(subState))}
      label={accountTabName}
      onClick={selectAccountTab}
    >
      {accountTabName}
    </Tab>
  );
}

function constructContactTabName(
  card: CardModel | undefined,
  contact: VisualContactModel,
  isPrivateContact: boolean,
): string {
  const nameToUse = card?.name || (contact.name ?? contact.contact.handle);
  if (isPrivateContact) {
    return nameToUse;
  }
  return nameToUse.split(' ')[0];
}

function constructAccountTabName(
  card: CardModel | undefined,
  contact: VisualContactModel,
  account?: AccountModel,
  selectedDomain?: DomainModel,
) {
  // If they do not have a card, extract the domain we have and show it
  if (!card) {
    const domain = extractDomainFromEmail(contact.contact.handle);
    if (commonProviderDomains.includes(domain) || domain === '') {
      return genericAccountTabName();
    }
    return domain;
  }

  // Show the account name if they are part of an account
  if (account) {
    return account.name;
  }

  if (!selectedDomain || selectedDomain.validityStatus !== DomainValidityStatusesEnum.VALID) {
    return genericAccountTabName();
  }

  return selectedDomain.name;
}

function genericAccountTabName() {
  return <Trans id="contact-details-plugin-header-account-tab-generic">Account</Trans>;
}
